import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHelpCircle, FiChevronDown, FiMail, FiMessageCircle, FiBook, FiVideo, FiPhone, FiUsers } = FiIcons;

const HelpPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: 'How do I get started with creating an agreement?',
      answer: 'Choose a template from the Templates page or start with a wizard on the homepage. Answer the guided questions, and our AI will generate a customized agreement draft.'
    },
    {
      question: 'Can I collaborate with others in real-time?',
      answer: 'Yes! Create a session, invite collaborators via the share link, and work together on answers. Changes are saved automatically and visible to all participants.'
    },
    {
      question: 'What types of agreements can I create?',
      answer: 'We support Partnership Agreements, Contractor Services, Employment Contracts, and Real Estate Agreements. Each with multiple specialized templates.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use bank-level encryption, secure authentication, and never share your data. All agreements are stored securely in our encrypted database.'
    },
    {
      question: 'Can I customize the generated documents?',
      answer: 'Yes, the generated documents are fully editable. You can modify terms, add custom clauses, and adjust wording before finalizing.'
    },
    {
      question: 'What if I need legal advice?',
      answer: 'While our AI provides guidance and templates, we recommend consulting with a qualified attorney for complex situations or significant financial commitments.'
    }
  ];

  const guides = [
    {
      icon: FiBook,
      title: 'Getting Started Guide',
      description: 'Complete walkthrough of creating your first agreement',
      link: '#'
    },
    {
      icon: FiUsers,
      title: 'Collaboration Features',
      description: 'Learn how to invite collaborators and work together',
      link: '#'
    },
    {
      icon: FiVideo,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides for all major features',
      link: '#'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiHelpCircle} className="text-3xl text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
            Help Center
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Get the support you need to create fair, professional agreements.
            Browse our guides, FAQs, and contact options below.
          </p>
        </motion.div>

        {/* Quick Guides */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Quick Start Guides</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {guides.map((guide, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => window.open(guide.link, '_blank')}
                className="glass-effect rounded-2xl p-8 cursor-pointer hover:shadow-glow transform hover:scale-105 transition-all duration-300 group"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-200">
                  <SafeIcon icon={guide.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{guide.title}</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">{guide.description}</p>
                <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                  Learn More
                  <SafeIcon icon={FiChevronDown} className="rotate-[-90deg]" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="glass-effect rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-slate-800">{faq.question}</h3>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`text-slate-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-effect rounded-2xl p-12 max-w-2xl mx-auto">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-green-100 to-blue-100 flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiMessageCircle} className="text-3xl text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Still Need Help?</h2>
            <p className="text-slate-600 mb-8 max-w-lg mx-auto">
              Our support team is here to help. Reach out to us through email or chat,
              and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@coagree.ai"
                className="flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiMail} />
                Email Support
              </a>
              <a
                href="tel:+1-555-0123"
                className="flex items-center justify-center gap-3 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <SafeIcon icon={FiPhone} />
                Call Us
              </a>
            </div>
            <p className="text-sm text-slate-500 mt-6">
              Response time: Usually within 24 hours
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HelpPage;